import { CheckIcon } from "@radix-ui/react-icons";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { XIcon } from "lucide-react";

import { AdvertisedPostItem } from "@/components/AdvertisedPostItem";
import { BlueskyIcon } from "@/components/BlueskyIcon";
import { CampaignAlgoAnalyticsBlock } from "@/components/CampaignAnalytics";
import { Avatar } from "@/components/ui/avatar";
import { Button, ButtonAnchor } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { Anchor, Blockquote } from "@/components/ui/typography";
import {
  allowStickPostOnCampaign,
  campaignQueryOptions,
  denyStickPostOnCampaign,
  getPendingPostsForCampaignAlgoQueryOptions,
  PendingCampaignPost,
  sponsoredPostsPerformanceQueryOptions,
} from "@/lib/api";
import { atUriToBskyUrl, cn } from "@/lib/utils";

export const Route = createFileRoute(
  "/app/_sidebar-layout/campaigns/$campaignId/feed/$algoId/_layout/",
)({
  component: RouteComponent,
  loader: async ({
    context: { queryClient },
    params: { campaignId, algoId },
  }) => {
    const campaign = await queryClient.ensureQueryData(
      campaignQueryOptions(parseInt(campaignId)),
    );
    const algo = campaign.algorithms.find((a) => a.id === parseInt(algoId))!;

    if (algo.details.state === "proposed") {
      throw redirect({
        to: `/app/campaigns/$campaignId/review/$algoId`,
        params: { campaignId, algoId },
      });
    } else if (algo.details.state === "denied") {
      throw redirect({ to: "/app/campaigns" });
    }

    void Promise.all([
      queryClient.ensureQueryData(
        sponsoredPostsPerformanceQueryOptions(
          parseInt(campaignId),
          parseInt(algoId),
        ),
      ),
    ]);
  },
});

function PendingCampaignPostRow({
  post: { post, id: stickyPostId },
  campaignId,
  algoId,
}: {
  post: PendingCampaignPost;
  campaignId: number;
  algoId: number;
}) {
  const acceptPendingPostMutation = useMutation({
    mutationFn: async (stickyPostId: number) => {
      await allowStickPostOnCampaign(campaignId, stickyPostId, algoId);
    },
  });
  const rejectPendingPostMutation = useMutation({
    mutationFn: async (stickyPostId: number) => {
      await denyStickPostOnCampaign(campaignId, stickyPostId, algoId);
    },
  });

  return (
    <div
      key={post.uri}
      className={cn(
        "border-b pb-4 mb-4 border-slate-6 dark:border-slatedark-6 last:border-b-0 last:mb-0 last:pb-0",
      )}
    >
      <div className="flex gap-4 items-center overflow-hidden">
        <Avatar size="sm" src={post.author.avatar} alt={post.author.handle} />
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <div className="text-xs text-slate-dim truncate">
            @{post.author.handle}
          </div>
          <div className="line-clamp-2 whitespace-pre-wrap">
            {post.record.text}
          </div>
        </div>

        <div className="flex items-center justify-end gap-1">
          <SimpleTooltip message="View on Bluesky">
            <ButtonAnchor
              variant="ghost"
              size="xsIcon"
              href={atUriToBskyUrl(post.uri)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="sr-only">View on Bluesky</span>
              <BlueskyIcon />
            </ButtonAnchor>
          </SimpleTooltip>

          <SimpleTooltip message="Reject">
            <Button
              disabled={rejectPendingPostMutation.isPending}
              onClick={() => {
                rejectPendingPostMutation.mutate(stickyPostId);
              }}
              className="flex-1"
              type="button"
              size="xsIcon"
              variant="destructive"
            >
              <span className="sr-only">Reject</span>
              <XIcon className="h-4 w-4" />
            </Button>
          </SimpleTooltip>
          <SimpleTooltip message="Accept">
            <Button
              disabled={acceptPendingPostMutation.isPending}
              onClick={() => {
                acceptPendingPostMutation.mutate(stickyPostId);
              }}
              className="flex-1"
              type="button"
              size="xsIcon"
              variant="success"
            >
              <span className="sr-only">Accept</span>
              <CheckIcon className="h-4 w-4" />
            </Button>
          </SimpleTooltip>
        </div>
      </div>
    </div>
  );
}

function RouteComponent(): React.ReactNode {
  const { campaignId, algoId } = Route.useParams();
  const { data: campaign } = useSuspenseQuery(
    campaignQueryOptions(parseInt(campaignId)),
  );
  const algo = campaign.algorithms.find((a) => a.id === parseInt(algoId))!;

  const { data: stickyPosts } = useSuspenseQuery(
    sponsoredPostsPerformanceQueryOptions(campaign.id, algo.id),
  );
  const { data: pendingPosts } = useSuspenseQuery(
    getPendingPostsForCampaignAlgoQueryOptions(campaign.id, algo.id),
  );

  return (
    <>
      {algo.details.metadata?.reason && (
        <div className="text-sm px-5 border-b border-slate-6 dark:border-slatedark-6 flex flex-col gap-3">
          <Blockquote>{algo.details.metadata.reason}</Blockquote>x
          <div>
            <span className="text-slate-dim font-semibold">From: </span>
            <Anchor
              href={`https://bsky.app/profile/${campaign.user.handle}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              @{campaign.user.handle}
            </Anchor>
          </div>
        </div>
      )}

      <CampaignAlgoAnalyticsBlock
        campaignId={campaign.id}
        algo={algo}
        isOwner={false}
      />

      {pendingPosts.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-medium">Pending Posts</h3>

          <div
            className={cn(
              "bg-ambera-3 dark:bg-amberdarka-3",
              "flex flex-col border-2 border-amber-6 dark:border-amberdark-6 rounded-lg p-4",
            )}
          >
            {pendingPosts.map((post) => (
              <PendingCampaignPostRow
                key={post.uri}
                post={post}
                campaignId={campaign.id}
                algoId={algo.id}
              />
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-medium">Posts in Campaign</h3>
        <div className="flex flex-col gap-2">
          {stickyPosts.map((stickyPost) => (
            <AdvertisedPostItem post={stickyPost} key={stickyPost.uri} />
          ))}
        </div>
      </div>
    </>
  );
}
